import React, { createContext, useContext, useEffect, useState } from 'react';
import { loginWithToken, getMe } from '../services/api';

const AuthContext = createContext(null);

/**
 * Provides cashflow client authentication state throughout the app.
 *
 * Tokens are stored in localStorage under the key `cashflow_token`.
 * On mount the context reads any existing token, validates it has not
 * expired (via JWT expiry), and optionally re-fetches the client profile.
 */
export function AuthProvider({ children }) {
  const [client, setClient] = useState(null);   // { client_id, name }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('cashflow_token');
    if (!stored) {
      setLoading(false);
      return;
    }

    // Quick client-side expiry check (no secret needed)
    try {
      const [, payload] = stored.split('.');
      const decoded = JSON.parse(atob(payload));
      if (decoded.exp && Date.now() / 1000 > decoded.exp) {
        localStorage.removeItem('cashflow_token');
        setLoading(false);
        return;
      }
      setClient({ client_id: decoded.sub, name: decoded.name || '' });
    } catch {
      localStorage.removeItem('cashflow_token');
    }
    setLoading(false);
  }, []);

  /**
   * Log in using a raw magic-link token.
   * Stores the resulting JWT and populates client state.
   *
   * @param {string} rawToken
   */
  async function login(rawToken) {
    setError(null);
    try {
      const data = await loginWithToken(rawToken);
      localStorage.setItem('cashflow_token', data.access_token);
      setClient({ client_id: data.client_id, name: data.name });
      return data;
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        'Invalid or expired link. Please contact your advisor.';
      setError(msg);
      throw new Error(msg);
    }
  }

  function logout() {
    localStorage.removeItem('cashflow_token');
    setClient(null);
  }

  return (
    <AuthContext.Provider value={{ client, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Convenient hook to consume the AuthContext. */
export function useAuth() {
  return useContext(AuthContext);
}
